import React, {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";

import {useContextHospi} from "context/ContextHospi";
import {convertTime} from "utils/helpers";
import {ChatsAPI} from "API/chatsApi";
import {API_CHATS} from "utils/constants";
import {
    IMessagesList,
    MessageTypeNames,
    TMessage,
    TMessageInvite,
    TMessageText,
    MessageActions,
    TArgFuncSendMsg, TConversation,
} from "interfaces/interfacesChat";
import {ChatNotFound, Loader, MessageSection, MessageSectionWrapper, MessageWrapper,} from "./styles";
import {Button, IcoEdit, TextPlain, TitleH2, TrashIcon,} from "components/generic";
import {theme} from "styles/theme";

import {
    DeletedChat,
    InvitationMessage,
    InvitationMessageResault, RemovedInterestOrHouse,
    SendMessageInfoSection,
} from "../SimpleComponents";
import Modal from "components/Modal";
import TextArea, {useTextArea} from "components/TextArea";
import {DialogWrapper} from "components/containers";
import {IMatchesEnum, IPerson, RoleUser} from "interfaces/intarfaces";
import {TextInfoContainer} from "../SimpleComponents/styles";
import {addMessageToList, transformInvitationToMsg, updateMessageList,} from "./logics";
import useSizeWindow from "utils/hooks/useWindowSize";

type TModeMsg = "normal" | "edited" | "deleted";

export type TModeInvitation = "cancelInvitation" | "decline" | "acceptOffer";

interface IPayload {
    type: MessageTypeNames;
    text: string;
}

export interface IMessageItem {
    body: IPayload;
    label_time: string;
    created_at: string;
    chat_id: string;
    isOwn: boolean;
    is_read: boolean;
    updated_at: string;
    mode: TModeMsg;
    handleMsg: TArgFuncSendMsg;
    setLatestRead?: (time: string[]) => void
    latestRead?: string[]
    handleMessage?: any
}

interface IMsgText {
    type: MessageTypeNames.TEXT_MSG;
    body: TMessageText;
}

interface IMsgInvite {
    type: MessageTypeNames.INVITE_MSG;
    body: TMessageInvite;
}

export type TBodyMessage = IMsgText | IMsgInvite | any;

export const MessagesList: React.FC<IMessagesList & { isPhone?: boolean, isInChat: boolean, setIsConvRead: any, currentNoChatId: string, activeConvId: string }> =
    React.memo(({ chater, newMessage, handleMessage, setLatestRead, isPhone, latestRead, setIsConvRead, activeConvId, currentNoChatId, isInChat, ...props }) => {
        const { t } = useTranslation("chats");
        const messagesAnchorRef = useRef<HTMLDivElement>(null);
        const [messageData, setMessageData] = useState<IMessageItem[]>([]);
        const [paginator, setPaginator] = useState<string>("");
        const [paginatorTrigger, setPaginatorTrigger] = useState(false);
        const device = useSizeWindow();
        const { userProfile, triggerChatLoad, setTriggerChatLoad, noChatId, setNoChatId } = useContextHospi();
        const { user_id: userID, user_type: userType } = userProfile as IPerson;
        const [isLoadingPrevMessages, setIsLoadingPrevMessages] = useState(false)
        const [showNoChat, setShowNoChat] = useState<boolean>(true)


        const loadMessages = (isNewChat: boolean = false) => {
            setIsLoadingPrevMessages(true)
            return new Promise((resolve, reject) => {
                ChatsAPI.get(
                    API_CHATS +
                    `chat-messages/${chater.chat_id}${!!paginator.length && !isNewChat ? "?before_dt=" + paginator : ""
                    }`
                )
                    .then((res) => {
                        if (
                            res.hasOwnProperty("messages") &&
                            !!(res.messages as TMessage[]).length
                        ) {
                            let [minDate, newArray] = updateMessageList(
                                isNewChat,
                                messageData,
                                res.messages as TMessage[],
                                handleMessage,
                                userID
                            );
                            setMessageData(newArray);
                            setPaginator(
                                (res.messages as TMessage[]).length === 5 ? minDate : ""
                            );
                            setPaginatorTrigger(false);
                            resolve(true);
                        } else {
                            !!!messageData.length && setMessageData([]);
                            setPaginator("");
                            setPaginatorTrigger(false);
                            resolve(false);
                        }
                        setIsLoadingPrevMessages(false)
                    })
                    .catch(() => {
                        setMessageData([]);
                        setPaginator("");
                        setPaginatorTrigger(false);
                        resolve(false);
                    });
            });

        };

        const uniqueIds = new Set();

        const unique = (arr: IMessageItem[]) => arr.filter(element => {
            if(element && arr.length) {
                const isDuplicate = uniqueIds.has(element.created_at);

                uniqueIds.add(element.created_at);

                if (!isDuplicate) {
                    return true;
                }

                return false;
            } else {
                return false;
            }
        });

        useEffect(() => {
                if(triggerChatLoad) {
                    loadMessages(true).then((response) => {
                        if (response) scrollToNew();
                        else setMessageData([]);
                    });
                    setTriggerChatLoad(false)
                }
        }, [triggerChatLoad])

        const handleInvitation = (mode: TModeInvitation) => {
            const payload: IMatchesEnum = {
                host_id: userType === "host" ? userID : chater.user_id,
                house_id: chater.house_id,
                student_id: userType === "student" ? userID : chater.user_id,
            };
            transformInvitationToMsg(mode, payload, handleMessage, {
                cancelInvitation: t("host.cancelInvitationMsg"),
                decline: t("student.cancelInvitationMsg"),
                acceptOffer: t("student.acceptInvitationMsg"),
            });
        };

        const scrollHandler = (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
            const element = e.currentTarget;
            if (paginatorTrigger && !!paginator.length && element.scrollTop < 100) {
                setPaginatorTrigger(false);
                loadMessages().then(
                    (nextScroll) =>
                        nextScroll &&
                            !!paginator.length &&
                                element.scrollTo({ top: 100 })
                );
            }
            if (element.scrollTop >= (element.scrollHeight <= 435 ? 65 : 95)) setPaginatorTrigger(true);
        };

        const scrollToNew = () =>
            setTimeout(() => {
                messagesAnchorRef.current?.scrollIntoView({ behavior: "smooth", block: 'nearest', inline: 'start' });
            }, 500);

        useEffect(() => {
            setPaginator("");
            !!chater.chat_id &&
                loadMessages(true).then((response) => {
                    if (response) scrollToNew();
                    else setMessageData([]);
                });
            !!!chater.chat_id && setMessageData([]);
            // eslint-disable-next-line
        }, [chater.chat_id]);

        useEffect(() => {
            if (
                newMessage &&
                newMessage.hasOwnProperty("action") &&
                newMessage.body.chat_id === chater.chat_id
            ) {
                addMessageToList(
                    newMessage,
                    messageData,
                    setMessageData,
                    handleMessage,
                    userID
                );
                scrollToNew();
            }
            // eslint-disable-next-line
        }, [newMessage]);

        return (
            <MessageSectionWrapper {...props}>
                {(isLoadingPrevMessages) && <Loader>
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                </Loader>}
                {/* onScroll={scrollHandler} */}
                <MessageSection isPhone onScroll={scrollHandler}>
                    {!!!messageData.length && !!chater.name.length && (
                        <SendMessageInfoSection
                            name={chater.name}
                            isHost={userType === "host"}
                        />
                    )}
                    {!!paginator.length && (
                        <div style={{ paddingBottom: device === "mobile" ? "120px" : "120px" }} />
                    )}
                    {unique(messageData)?.map((item: IMessageItem, index: number) => (
                        <React.Fragment key={index}>
                            {!!item.label_time.length && (
                                <p style={{ textAlign: item.isOwn ? "right" : "left" }}>
                                    {item.label_time}
                                </p>
                            )}
                            {([MessageTypeNames.REMOVE_INTEREST, MessageTypeNames.PLACE_NO_LONGER_AVAILABLE].includes(item.body.type) || ['Student removed interest on the house', 'House demoted to draft'].includes(item.body.text)) ? (
                                <RemovedInterestOrHouse
                                   {...item}
                                   latestRead={latestRead}
                                   setLatestRead={setLatestRead}
                                   isHost={userType === "host"}
                                   name={chater.name}
                                   interval={item.body.text}
                                   handleInvitation={handleInvitation}
                               />
                            ) : item.body.type === MessageTypeNames.INVITE_MSG ? (
                                item.body.text === "removeTenant" ? (
                                    <Message {...item} handleMessage={handleMessage} isInChat={isInChat} user_type={userType} latestRead={latestRead} setLatestRead={setLatestRead} />
                                ) : (
                                    <InvitationMessage
                                        {...item}
                                        latestRead={latestRead}
                                        setLatestRead={setLatestRead}
                                        isHost={userType === "host"}
                                        name={chater.name}
                                        interval={item.body.text}
                                        handleInvitation={handleInvitation}
                                    />
                                )
                            ) : (item.body.type === MessageTypeNames.REMOVE_CHAT_MESSAGE) ? (<DeletedChat {...item} latestRead={latestRead} setLatestRead={setLatestRead} text={item.body.text.includes('host') ? t('chatRemovedHost') : t('chatRemovedStudent')} />) : (
                                <Message {...item} handleMessage={handleMessage} isInChat={isInChat} user_type={userType} latestRead={latestRead} setLatestRead={setLatestRead} />
                            )}
                        </React.Fragment>
                    ))}
                    <div ref={messagesAnchorRef} style={{ paddingTop: "8px" }} />
                </MessageSection>
               {
                   (currentNoChatId === activeConvId) && <ChatNotFound>
                       {t('hostRemovedMatch')}
                   </ChatNotFound>
               }
            </MessageSectionWrapper>
        );
    });

const Message: React.FC<IMessageItem & { user_type: RoleUser, isInChat: boolean }> = React.memo(
    (props) => {
        const { setLatestRead, isInChat, latestRead } = props;
        const [showModal, setShowModal] = useState<number>(0);
        const { t } = useTranslation("chats");
        const [message, setMessage] = useState({
            text: props.body.text ?? "",
            datetime: convertTime(props.updated_at),
            mode: props.mode,
        });

        const useIsInViewport = () => {
            const elementRef = useRef<any>(null)
            const [isInViewPort, setIsInViewPort] = useState(false)

            useEffect(() => {
                const observer = new IntersectionObserver((entries) => {
                    const isInViewPort = !!entries[0]?.isIntersecting

                    setIsInViewPort(isInViewPort)
                })
                if (elementRef.current) {
                    //@ts-ignore
                    observer.observe(elementRef.current)
                }

                return () => observer.disconnect()
            }, [])

            return { elementRef, isInViewPort }
        }

        const { elementRef: viewportRef, isInViewPort } = useIsInViewport()

        useEffect(() => {
            if (isInViewPort && !props.isOwn && !props.is_read && !props.latestRead!.includes(props.created_at)) {
                //@ts-ignore
                setLatestRead(prev => [...prev, props.created_at])
                props.handleMsg('read',
                    MessageActions.MESSAGE_READ,
                    props.created_at)
            }
        }, [isInViewPort, props.created_at, props.handleMsg, props.isOwn, setLatestRead, viewportRef, props.latestRead])


        const textMsg = useTextArea("");

        useEffect(() => {
            textMsg.onChange(props.body.text);
            setMessage((prev) => ({
                ...prev,
                text: props.body.text,
                mode: props.mode,
                datetime: convertTime(props.updated_at),
            }));
            // eslint-disable-next-line
        }, [props]);

        const editMessage = () => {
            props.handleMsg(
                textMsg.value,
                MessageActions.MESSAGE_UPDATE,
                props.created_at
            );
            setShowModal(0);
        };

        const deleteMessage = () => {
            props.handleMsg(
                t("textDeletedMessage"),
                MessageActions.MESSAGE_DELETE,
                props.created_at
            );
            setShowModal(0);
        };

        if (message.text === "removeTenant")
            return (
                <TextInfoContainer
                    alignCenter
                    border
                    color={theme.colors.place}
                    background={theme.colors.text}
                >
                    <p style={{ margin: 0 }}>
                        {props.user_type === "student"
                            ? t("student.removedTenants")
                            : t("host.removedTenants")}
                    </p>
                </TextInfoContainer>
            );

        if (
            [
                t("host.cancelInvitationMsg"),
                t("student.acceptInvitationMsg"),
                t("student.cancelInvitationMsg"),
            ].includes(message.text)
        )
            return (
                <>
                    {props.user_type === "student" &&
                        (message.text === t("host.cancelInvitationMsg") ? (
                            <InvitationMessageResault {...props} latestRead={latestRead} isHost={false} success={false} />
                        ) : message.text === t("student.acceptInvitationMsg") ? (
                            <InvitationMessageResault {...props} latestRead={latestRead} isHost={false} success={true} />
                        ) : (
                            <TextInfoContainer
                                alignCenter
                                border
                                color={theme.colors.place}
                                background={theme.colors.text}
                            >
                                <p style={{ margin: 0 }}>{message.text}</p>
                            </TextInfoContainer>
                        ))}

                    {props.user_type === "host" &&
                        (message.text === t("student.acceptInvitationMsg") ? (
                            <InvitationMessageResault {...props} latestRead={latestRead} isHost={true} success={true} />
                        ) : message.text === t("student.cancelInvitationMsg") ? (
                            <div>
                                <InvitationMessageResault {...props} latestRead={latestRead} isHost={true} success={false} />
                            </div>
                        ) : (
                            <TextInfoContainer
                                alignCenter
                                border
                                color={theme.colors.place}
                                background={theme.colors.text}
                            >
                                <p style={{ margin: 0 }}>{message.text}</p>
                            </TextInfoContainer>
                        ))}
                </>
            );

        return (
            <MessageWrapper isMy={props.isOwn} isDel={message.mode === "deleted"}>
                {props.isOwn && !(message.mode === "deleted") && (
                    <div className="editMsg">
                        <IcoEdit
                            size="1.5em"
                            color={theme.colors.thirdly}
                            onClick={() => setShowModal(1)}
                        />
                        <TrashIcon
                            size="1.5em"
                            style={{ marginLeft: "1.5rem" }}
                            color={theme.colors.thirdly}
                            onClick={() => setShowModal(2)}
                        />
                    </div>
                )}
                {message.mode === "deleted" ? (
                    <p>
                        {t("textDeletedMessage")}
                        <span
                            style={{ color: theme.colors.place }}
                        >{` (${message.datetime})`}</span>
                    </p>
                ) : (
                    <p ref={viewportRef}>
                        {message.text}
                        {message.mode === "edited" && (
                            <span>{` (Edited ${message.datetime})`}</span>
                        )}
                    </p>
                )}
                {(!props.is_read && !props.isOwn && !isInChat) && <div id='unread' />}
                {!!showModal && (
                    <Modal isShow={!!showModal} handleClose={() => setShowModal(0)}>
                        {showModal === 1 ? (
                            <DialogWrapper style={{ maxWidth: "620px", width: "90vw" }}>
                                <TitleH2>{t("editMsg")}</TitleH2>
                                <TextArea {...textMsg} />
                                <div className="buttons">
                                    <Button normal onClick={() => setShowModal(0)}>
                                        {t("butCancel")}
                                    </Button>
                                    <Button danger onClick={editMessage}>
                                        {t("butEdit")}
                                    </Button>
                                </div>
                            </DialogWrapper>
                        ) : (
                            <DialogWrapper>
                                <TitleH2>{t("deleteMsg")}</TitleH2>
                                <TextPlain>{t("textDelete")}</TextPlain>

                                <div className="buttons">
                                    <Button normal onClick={() => setShowModal(0)}>
                                        {t("butCancel")}
                                    </Button>
                                    <Button danger onClick={deleteMessage}>
                                        {t("butDelete")}
                                    </Button>
                                </div>
                            </DialogWrapper>
                        )}
                    </Modal>
                )}
            </MessageWrapper>
        );
    }
);
